import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mountain } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import type { InsightRareSend } from '../../../types/insight';

interface RareSendsListProps {
  sends: InsightRareSend[];
}

/**
 * The most convincing part of the tab, and the most robust: each row is
 * per-climb evidence rather than a statistic, so it reads the same whether the
 * platform has fifty climbers or fifty thousand.
 *
 * The backend only sends climbs enough people have actually tried — a rarity
 * inferred from the grade alone would surface climbs the reader knows are easy.
 */
export default function RareSendsList({ sends }: RareSendsListProps) {
  const { t } = useTranslation();

  if (sends.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('userProfile.insights.rareSends.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sends.map((send) => (
          <RareSendRow key={send.climb.id} send={send} />
        ))}
      </CardContent>
    </Card>
  );
}

function RareSendRow({ send }: { send: InsightRareSend }) {
  const { t } = useTranslation();
  const { climb } = send;
  const climbPath =
    climb.location && climb.slug ? `/gyms/${climb.location.slug}/climbs/${climb.slug}` : null;

  const name = (
    <span className="font-medium">
      {climb.name}
      {climb.difficulty && (
        <Badge variant="outline" className="ml-2 text-xs">
          {climb.difficulty}
        </Badge>
      )}
    </span>
  );

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
        {climb.image_small_url ? (
          <img src={climb.image_small_url} alt={climb.name} className="h-full w-full object-cover" />
        ) : (
          <Mountain className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        {climbPath ? (
          <Link to={climbPath} className="transition-colors hover:text-primary">
            {name}
          </Link>
        ) : (
          name
        )}
        <p className="text-xs text-muted-foreground">
          {t('userProfile.insights.rareSends.ratio', {
            senders: send.senders_count,
            attempters: send.attempters_count,
          })}
          {climb.location && ` · ${climb.location.name}`}
        </p>
      </div>

      <EffortLabel send={send} />
    </div>
  );
}

/** How the ascent went — the second half of what makes a send impressive. */
function EffortLabel({ send }: { send: InsightRareSend }) {
  const { t } = useTranslation();

  if (send.send_type === 'on_sight') {
    return <span className="text-xs font-medium text-emerald-600">{t('climbLog.onSight')}</span>;
  }
  if (send.send_type === 'flash') {
    return <span className="text-xs font-medium text-green-600">{t('climbLog.flash')}</span>;
  }
  if (send.send_type === 'worked' && send.attempts_count) {
    return (
      <span className="text-xs tabular-nums text-muted-foreground">
        {t('userProfile.insights.rareSends.attempts', { count: send.attempts_count })}
      </span>
    );
  }
  return null;
}
